import React, { useCallback, useMemo, useState, useEffect, useRef } from "react"
import { useTheme } from "@emotion/react"
import { captureException, captureMessage } from "@sentry/minimal"
import { useHistory } from "react-router-dom"

import {
	CustomerPartOrderPost,
	CustomerPartOrderPostSchema,
	isAxiosError,
	makeApiErrorMessage,
	PaymentMethod,
	PaymentMethodBundle,
	SquareCardBrand,
	useAuth,
	useCreateCustomerPartOrder,
	useCreatePunchoutOrder,
	useIsUser,
	UserId,
	useSite,
	useSites,
	useUserProfile,
} from "@ncs/ncs-api"
import { isCanadianProvince, noFalsy, unpythonify, validatePhone } from "@ncs/ts-utils"
import {
	Card,
	encodeUrlState,
	Paragraph,
	useSquareCardContext,
	Flag,
	useIsFeatureFlagOn,
	Icon,
	useScreenSizeMatch,
} from "@ncs/web-legos"
import {
	outerContainerStyle,
	stepContainerStyle,
	titleContainer,
	styleStepNumber,
	styleStepNumberText,
	styleCollapseIcon,
} from "./styles"

import { useOrderTotals, useShopContext } from "~/contexts"
import { PageContentWrapper, SupportIssueModal } from "~/shared-components"
import { ShopUrlState } from "~/views/shop/shop/ShopEcomm"

import { PunchOrderFinishedUrlState } from "../punchout-order-finished/PunchoutOrderFinished"
import { makeNewOrderLineItems } from "./checkout-util"
import {
	OrderSummaryEcomm,
	ChoosePaymentMethodEcomm,
	ChooseShippingAddressEcomm,
	ChooseShippingMethodsEcomm,
	OrderSummaryEcommTariff,
} from "./components"
import { VoucherInputHandle } from "./components/voucherify/VoucherInput"

import { useUpdatePartTotals } from "../../../util"
import ErrorPaymentModal from "./components/ErrorPaymentModal"
import { handleSquareErrorResponse } from "~/util/squareErrors"

export type VoucherifyDiscount = {
	totalAmount: number
	totalDiscountAmount: number
}

export const CheckoutEcomm: React.FC = () => {
	const isDb = useIsUser(UserId.DrivenBrands)
	const auth = useAuth()
	const history = useHistory()
	const isPunchout = !!auth.punchOutSessionId
	const isMr = useIsUser(UserId.MisterCarwash)
	const isPunchoutAndMr = isPunchout && isMr

	const [sites] = useSites()
	const hasAddress = isPunchoutAndMr || (sites ?? null)
	const theme = useTheme()
	const isTariffEnabled = useIsFeatureFlagOn(Flag.TARIFFS)

	useUpdatePartTotals()

	const [{ cart, checkout }, shopDispatch] = useShopContext()

	const {
		totalItemCount,
		shipmentPartsSubtotal,
		shippingTotalWithoutAccessorials,
		shippingTotal,
		orderGrandTotal,
		totalTax,
		tariffEstimate,
	} = useOrderTotals(true, isTariffEnabled, isPunchoutAndMr)

	useEffect(() => {
		const phone = sites?.map((s) => s.phone).find((n) => n)
		if (phone) {
			shopDispatch({
				type: "set checkout phone",
				payload: phone,
			})
		}
	}, [sites, shopDispatch])

	const { card, verifyBuyer } = useSquareCardContext()

	const isMobile = useScreenSizeMatch("sm")
	const [stepOneOpen, setStepOneOpen] = useState(!isMobile)
	const [stepTwoOpen, setStepTwoOpen] = useState(!isMobile)
	const [stepThreeOpen, setStepThreeOpen] = useState(!isMobile)

	// Refs for scroll to accordion
	const stepOneRef = useRef<HTMLDivElement>(null)
	const stepTwoRef = useRef<HTMLDivElement>(null)
	const stepThreeRef = useRef<HTMLDivElement>(null)

	// Helper function for smooth scroll
	const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
		setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50)
	}

	// Toggle handlers
	const handleStepOneToggle = () => {
		if (isMobile) {
			if (!stepOneOpen) {
				setStepOneOpen(true)
				setStepTwoOpen(false)
				setStepThreeOpen(false)
				scrollToRef(stepOneRef)
			} else {
				setStepOneOpen(false)
			}
		} else {
			if (!stepOneOpen && (stepTwoOpen || stepThreeOpen)) {
				setStepTwoOpen(false)
				setStepThreeOpen(false)
			}
			setStepOneOpen(!stepOneOpen)
		}
	}

	const handleStepTwoToggle = () => {
		if (isMobile) {
			if (!stepTwoOpen) {
				setStepTwoOpen(true)
				setStepOneOpen(false)
				setStepThreeOpen(false)
				scrollToRef(stepTwoRef)
			} else {
				setStepTwoOpen(false)
			}
		} else {
			if (!stepTwoOpen && stepOneOpen) {
				setStepOneOpen(false)
			}
			setStepTwoOpen(!stepTwoOpen)
		}
	}

	const handleStepThreeToggle = () => {
		if (isMobile) {
			if (!stepThreeOpen) {
				setStepThreeOpen(true)
				setStepOneOpen(false)
				setStepTwoOpen(false)
				scrollToRef(stepThreeRef)
			} else {
				setStepThreeOpen(false)
			}
		} else {
			if (!stepThreeOpen && (stepOneOpen || stepTwoOpen)) {
				setStepOneOpen(false)
				setStepTwoOpen(false)
			}
			setStepThreeOpen(!stepThreeOpen)
		}
	}

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [hasShippingMethod, setHasShippingMethod] = useState(false)

	const createOrder = useCreateCustomerPartOrder()
	const createPunchoutOrder = useCreatePunchoutOrder()

	const [profile] = useUserProfile(auth.user?.id)
	const isGuest = profile?.isGuest
	const [shipToSite] = useSite(checkout.alternateAddressId ?? checkout.shipToSiteId)

	const [voucherifyDiscounts, setVoucherifyDiscounts] = useState<VoucherifyDiscount | null>(null)
	const voucherInputRef = useRef<VoucherInputHandle>(null)

	const isSectionAvailable =
		isPunchoutAndMr ||
		!!(
			(checkout?.shipToSiteId ||
				checkout?.alternateAddress ||
				checkout?.alternateAddressId) &&
			(isDb || validatePhone(checkout?.phone))
		)

	useEffect(() => {
		if (isMobile) {
			if (hasShippingMethod) {
				setStepOneOpen(false)
				setStepTwoOpen(false)
				setStepThreeOpen(true)
				setTimeout(
					() =>
						stepThreeRef.current?.scrollIntoView({
							behavior: "smooth",
							block: "start",
						}),
					50
				)
			} else if (isSectionAvailable) {
				setStepOneOpen(false)
				setStepTwoOpen(true)
				setStepThreeOpen(false)
				setTimeout(
					() =>
						stepTwoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
					50
				)
			} else {
				setStepOneOpen(true)
				setStepTwoOpen(false)
				setStepThreeOpen(false)
			}
		} else {
			setStepOneOpen(true)
			setStepTwoOpen(true)
			setStepThreeOpen(true)
		}
	}, [isMobile, isSectionAvailable, hasShippingMethod])

	const partOrderPostData = useMemo(() => {
		if (!checkout.shipToSiteId && !checkout.alternateAddress && !checkout.alternateAddressId) {
			return null
		}
		if (!isDb && !checkout.phone) {
			return null
		}

		const order: CustomerPartOrderPost = {
			// If you're using an alternate address, then we only need the ship to if you're billing to account.
			shipToCustomerId:
				checkout.alternateAddress ?
					checkout.selectedPaymentMethod === PaymentMethod.CUSTOMER_ACCOUNT ?
						(checkout.shipToSiteId ?? undefined)
					:	undefined
				:	(checkout.shipToSiteId ?? undefined),
			purchaseOrderNumber: checkout.purchaseOrder,
			comment: checkout.comment,
			lineItems: makeNewOrderLineItems(
				cart,
				checkout.shipments,
				checkout.accessorials,
				shippingTotalWithoutAccessorials,
				isTariffEnabled ? tariffEstimate : undefined,
				voucherifyDiscounts,
				isPunchoutAndMr
			),
			sessionId: auth.punchOutSessionId,
			alternateAddressId: checkout.alternateAddressId ?? undefined,
			alternateAddress:
				checkout.alternateAddress ?
					{
						address1: checkout.alternateAddress.address1 ?? null,
						address2: checkout.alternateAddress.address2,
						city: checkout.alternateAddress.city,
						state: checkout.alternateAddress.state,
						postalcode: checkout.alternateAddress.zip,
					}
				:	undefined,
			contactPhone: checkout.phone ?? undefined,
			isGuest: isGuest,
		}

		return order
	}, [
		isDb,
		cart,
		checkout.purchaseOrder,
		checkout.accessorials,
		checkout.comment,
		checkout.shipToSiteId,
		checkout.alternateAddress,
		checkout.alternateAddressId,
		checkout.shipments,
		shippingTotalWithoutAccessorials,
		auth.punchOutSessionId,
		checkout.selectedPaymentMethod,
		checkout.phone,
		tariffEstimate,
		isTariffEnabled,
		isGuest,
		voucherifyDiscounts,
	])

	const beginOrderPlacement = async () => {
		if (!partOrderPostData) {
			console.error("Trying to place order but order data was not complete")
			return
		}

		// Turn on isSubmitting here. Each method is responsible for turning it off later.
		setIsSubmitting(true)

		if (isPunchout) {
			void postPunchoutOrder(partOrderPostData)
			return
		}

		switch (checkout.selectedPaymentMethod) {
			case PaymentMethod.SQUARE_CARD: {
				if (!card) {
					throw new Error("Square card instance not found.")
				}

				const tokenResult = await card.tokenize()
				if (tokenResult.status === "OK") {
					if (!tokenResult.token) throw new Error("Token not found on token result")

					// Now that we've got the nonce, we need to call a separate Square endpoint
					// to verify buyer details and charge amount.
					if (!profile) throw new Error("User profile not loaded")

					const verificationResult = await verifyBuyer(tokenResult.token, {
						amount: String(orderGrandTotal),
						currencyCode: "USD",
						intent: "CHARGE",
						billingContact: {
							familyName: profile.lastName,
							givenName: profile.firstName,
							email: profile.email,
							countryCode: "US",
							city: shipToSite?.city ?? checkout.alternateAddress?.city,
							addressLines: noFalsy([
								shipToSite?.address1 ?? checkout.alternateAddress?.address1,
								shipToSite?.address2 ?? checkout.alternateAddress?.address2,
							]),
							state: shipToSite?.state ?? checkout.alternateAddress?.state,
							postalCode: shipToSite?.postalCode ?? checkout.alternateAddress?.zip,
						},
					})
					if (!verificationResult) throw new Error("Error verifying card.")

					// Now put it all together and send to server.
					const { details } = tokenResult
					if (!details?.card) throw new Error("Card details not found in token result.")

					const payment: PaymentMethodBundle = {
						method: PaymentMethod.SQUARE_CARD,
						data: {
							last4: details.card.last4,
							expMonth: details.card.expMonth,
							expYear: details.card.expYear,
							brand: details.card.brand as unknown as SquareCardBrand,
							nonce: tokenResult.token,
							verificationToken: verificationResult.token,
							billingPostalCode: tokenResult.details?.billing?.postalCode ?? null,
							digitalWalletType: "NONE", // If we ever support Google Pay or Apple Pay, this'll need to change...
						},
					}

					void postNewOrder({
						...partOrderPostData,
						payment,
					})
				} else {
					console.error("Error tokenizing")
					setIsSubmitting(false)
				}
				break
			}
			case PaymentMethod.STORED_BANK_ACCOUNT: {
				if (!checkout.selectedBankAccount?.id) {
					throw new Error(
						"Trying to check out with stored bank account, but there isn't one selected in checkout state."
					)
				}

				void postNewOrder({
					...partOrderPostData,
					payment: {
						method: PaymentMethod.STORED_BANK_ACCOUNT,
						data: checkout.selectedBankAccount.id,
					},
				})
				break
			}
			case PaymentMethod.STORED_CARD: {
				if (!checkout.selectedCard?.id) {
					throw new Error(
						"Trying to check out with stored card but there isn't one selected in checkout state."
					)
				}

				void postNewOrder({
					...partOrderPostData,
					payment: {
						method: PaymentMethod.STORED_CARD,
						data: checkout.selectedCard.id,
					},
				})
				break
			}
			case PaymentMethod.CUSTOMER_ACCOUNT: {
				void postNewOrder(partOrderPostData)
				break
			}
			default: {
				console.error("No handler for selected payment method")
			}
		}
	}

	const postNewOrder = async (orderData: CustomerPartOrderPost) => {
		try {
			setIsSubmitting(true)
			setErrorMessage(null)
			const parseResult = CustomerPartOrderPostSchema.safeParse(orderData)
			if (!parseResult.success) {
				console.warn("Order data did not pass parse check", orderData)
				captureMessage("Order data post failed parsing! Posting anyway...", {
					extra: { orderData },
				})
			}

			const response = await createOrder(orderData)

			// Redeem vouchers after successful order placement
			if (voucherInputRef.current) {
				await voucherInputRef.current.redeemVouchers()
			}

			// Capture shipping method before resetting checkout state
			const firstShipmentService = checkout.shipments?.[0]?.service ?? null
			shopDispatch({
				type: "reset checkout state",
			})
			const newOrder = unpythonify(response.data)
			history.replace({
				pathname: "/shop",
				search: encodeUrlState<ShopUrlState>({
					fromOrder: newOrder.orderId.toString(),
					shippingMethod: firstShipmentService,
				}),
			})
		} catch (e) {
			if (isAxiosError(e)) {
				const responseData = e.response?.data as { error?: string; payment?: string }
				if (responseData) {
					setErrorMessage(handleSquareErrorResponse(responseData))
					setIsErrorModalOpen(true)
				}
			}
		} finally {
			setIsSubmitting(false)
		}
	}

	const postPunchoutOrder = async (orderData: CustomerPartOrderPost) => {
		try {
			setIsSubmitting(true)
			setErrorMessage(null)
			const parseResult = CustomerPartOrderPostSchema.safeParse(orderData)
			if (!parseResult.success) {
				console.warn("Punchout order data did not pass parse check", orderData)
				captureMessage("Punchout order data post failed parsing! Posting anyway...", {
					extra: { orderData },
				})
			}
			const response = await createPunchoutOrder(orderData)

			// Redeem vouchers after successful punchout order placement
			if (voucherInputRef.current) {
				await voucherInputRef.current.redeemVouchers()
			}

			const redirectUrl = response.data
			// (The punchout order placed page will clear out shop context local storage and logout)
			history.replace({
				pathname: "/shop/punchout-order-finished",
				search: encodeUrlState<PunchOrderFinishedUrlState>({
					redirect: redirectUrl,
				}),
			})
		} catch (e) {
			setErrorMessage(makeApiErrorMessage(e))
			captureException(e)
			setIsSubmitting(false)
		}
	}

	const updateComment = useCallback(
		(newValue: string | null) => {
			shopDispatch({
				type: "set checkout comment",
				payload: newValue || null,
			})
		},
		[shopDispatch]
	)

	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
	const [showTicketModal, setShowTicketModal] = useState(false)
	const isCanada = useMemo(() => {
		return isCanadianProvince(checkout.alternateAddress?.state)
	}, [checkout.alternateAddress?.state])

	return (
		<PageContentWrapper
			title="Checkout"
			breadcrumbs={[{ name: "Shop", to: "/shop" }, { name: "Checkout" }]}
		>
			<Paragraph fontSize={1.5} mt={1.5} textAlign="left" bold>
				Checkout
			</Paragraph>

			<div css={outerContainerStyle}>
				<div>
					<div ref={stepOneRef}>
						<Card css={stepContainerStyle(theme, !!hasAddress)}>
							<div css={titleContainer}>
								<div css={styleStepNumber(theme, true)}>
									<div>1</div>
								</div>

								<div css={styleStepNumberText(theme, true)}>Shipping Address</div>
								<button css={styleCollapseIcon} onClick={handleStepOneToggle}>
									{stepOneOpen ?
										<Icon icon="chevron-up" width={16} />
									:	<Icon icon="chevron-down" width={16} />}
								</button>
							</div>

							{(stepOneOpen || isPunchoutAndMr) && (
								<div className="step-content">
									<ChooseShippingAddressEcomm updateComment={updateComment} />
								</div>
							)}
						</Card>
					</div>

					<div ref={stepTwoRef}>
						<Card css={stepContainerStyle(theme, !!hasAddress)}>
							<div css={titleContainer}>
								<div css={styleStepNumber(theme, isSectionAvailable)}>
									<div>2</div>
								</div>
								<div css={styleStepNumberText(theme, isSectionAvailable)}>
									Shipping Method
								</div>
								{isSectionAvailable ?
									<button css={styleCollapseIcon} onClick={handleStepTwoToggle}>
										{stepTwoOpen ?
											<Icon icon="chevron-up" width={16} />
										:	<Icon icon="chevron-down" width={16} />}
									</button>
								:	<button css={styleCollapseIcon}>
										<Icon icon="chevron-up" width={16} color="gray" />
									</button>
								}
							</div>

							{((isSectionAvailable && stepTwoOpen) || isPunchoutAndMr) && (
								<div className="step-content">
									<ChooseShippingMethodsEcomm
										setHasShippingMethod={setHasShippingMethod}
									/>
								</div>
							)}
						</Card>
					</div>

					<div ref={stepThreeRef}>
						<Card css={stepContainerStyle(theme, !!hasAddress && hasShippingMethod)}>
							<div css={titleContainer}>
								<div css={styleStepNumber(theme, isSectionAvailable)}>
									<div>3</div>
								</div>

								<div css={styleStepNumberText(theme, isSectionAvailable)}>
									Payment Method
								</div>
								{isSectionAvailable ?
									<button
										css={styleCollapseIcon}
										onClick={handleStepThreeToggle}
									>
										{stepThreeOpen ?
											<Icon icon="chevron-up" width={16} />
										:	<Icon icon="chevron-down" width={16} />}
									</button>
								:	<button css={styleCollapseIcon}>
										<Icon icon="chevron-up" width={16} color="gray" />
									</button>
								}
							</div>

							{(isPunchoutAndMr || (isSectionAvailable && stepThreeOpen)) && (
								<div className="step-content">
									{!isPunchout ?
										<ChoosePaymentMethodEcomm />
									:	<Paragraph color="secondary">
											{isPunchoutAndMr ?
												"Your payment will be billed to your master account."
											:	"Payment will be handled by the client you initiated this session from."
											}
										</Paragraph>
									}
								</div>
							)}
						</Card>
					</div>
				</div>
				<div>
					{isTariffEnabled ?
						<OrderSummaryEcommTariff
							profile={profile}
							isDb={isDb}
							shipToSite={shipToSite}
							setErrorMessage={setErrorMessage}
							isCanada={isCanada}
							onPlaceOrder={beginOrderPlacement}
							isSubmitting={isSubmitting}
							orderGrandTotal={orderGrandTotal}
							totalItemCount={totalItemCount}
							shipmentPartsSubtotal={shipmentPartsSubtotal}
							shippingTotal={shippingTotal}
							totalTax={totalTax}
							tariffEstimate={tariffEstimate.amount ?? 0}
							setVoucherifyDiscounts={setVoucherifyDiscounts}
							voucherifyDiscounts={voucherifyDiscounts}
							voucherInputRef={voucherInputRef}
						/>
					:	<OrderSummaryEcomm
							profile={profile}
							isDb={isDb}
							shipToSite={shipToSite}
							setErrorMessage={setErrorMessage}
							isCanada={isCanada}
							onPlaceOrder={beginOrderPlacement}
							isSubmitting={isSubmitting}
							orderGrandTotal={orderGrandTotal}
							totalItemCount={totalItemCount}
							shipmentPartsSubtotal={shipmentPartsSubtotal}
							shippingTotal={shippingTotal}
							totalTax={totalTax}
							setVoucherifyDiscounts={setVoucherifyDiscounts}
							voucherifyDiscounts={voucherifyDiscounts}
							voucherInputRef={voucherInputRef}
						/>
					}
				</div>
			</div>
			<ErrorPaymentModal
				errorMessage={errorMessage ?? ""}
				isOpen={isErrorModalOpen}
				onClose={() => {
					setIsErrorModalOpen(false)
				}}
				onContactSupport={() => setShowTicketModal(true)}
			/>
			<SupportIssueModal
				isOpen={showTicketModal}
				onClose={() => setShowTicketModal(false)}
			/>
		</PageContentWrapper>
	)
}
